/**
 * DatePicker extended component customization for WeekPicker functionality.
 * This component is extracted from the demos from official docs of @materialui/picker
 * ref: https://material-ui-pickers.dev/demo/datepicker#customization
 * Updates:
 * Added a wrapper of <MuiPickersUtilsProvider> to tackle provider error.
 * @author: "shaurabh.shah.prtnr@ironmountain.com"
 *
 */

import clsx from "clsx";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import isSameDay from "date-fns/isSameDay";
import endOfWeek from "date-fns/endOfWeek";
import React, { PureComponent } from "react";
import startOfWeek from "date-fns/startOfWeek";
import isWithinInterval from "date-fns/isWithinInterval";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { createStyles } from "@material-ui/styles";
import { IconButton, withStyles } from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";

let state, handleWeekChange, formatWeekSelectLabel, renderWrappedWeekDay;
class WeekPicker extends PureComponent {
  state = {
    selectedDate: this.props.startDate ? this.props.startDate : new Date(),
  };

  handleWeekChange = (date) => {
    this.setState({ selectedDate: startOfWeek(date) });
    this.props.weekChangeHandler(startOfWeek(date));
  };

  formatWeekSelectLabel = (date, invalidLabel) => {
    let dateClone = date;

    return dateClone && isValid(dateClone)
      ? `Week of ${format(startOfWeek(dateClone), "MMM do")}`
      : invalidLabel;
  };

  renderWrappedWeekDay = (date, selectedDate, dayInCurrentMonth) => {
    const { classes } = this.props;
    let dateClone = date;
    let selectedDateClone = selectedDate;

    const start = startOfWeek(selectedDateClone);
    const end = endOfWeek(selectedDateClone);

    const dayIsBetween = isWithinInterval(dateClone, { start, end });
    const isFirstDay = isSameDay(dateClone, start);
    const isLastDay = isSameDay(dateClone, end);

    const wrapperClassName = clsx({
      [classes.highlight]: dayIsBetween,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    });

    const dayClassName = clsx(classes.day, {
      [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
      [classes.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
    });

    const disabled =
      this.props.minDate &&
      this.props.maxDate &&
      (moment(date).diff(startOfWeek(new Date(this.props.minDate))) < 0 ||
        moment(date).diff(endOfWeek(new Date(this.props.maxDate)))) > 0
        ? true
        : false;

    return (
      <div className={wrapperClassName}>
        <IconButton className={dayClassName} disabled={disabled}>
          <span> {format(dateClone, "d")} </span>
        </IconButton>
      </div>
    );
  };

  render() {
    const { selectedDate } = this.state;

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <DatePicker
          label={this.props.lbl}
          minDate={
            this.props.minDate && startOfWeek(new Date(this.props.minDate))
          }
          maxDate={this.props.maxDate && endOfWeek(new Date(this.props.maxDate))}
          value={selectedDate}
          onChange={this.handleWeekChange}
          renderDay={this.renderWrappedWeekDay}
          labelFunc={this.formatWeekSelectLabel}
          TextFieldComponent={this.props.renderInput}
        />
      </MuiPickersUtilsProvider>
    );
  }
}

const styles = createStyles((theme) => ({
  dayWrapper: {
    position: "relative",
  },
  day: {
    width: 36,
    height: 36,
    fontSize: theme.typography.caption.fontSize,
    margin: "0 2px",
    color: "inherit",
  },
  customDayHighlight: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "2px",
    right: "2px",
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: "50%",
  },
  nonCurrentMonthDay: {
    color: theme.palette.text.disabled,
  },
  highlightNonCurrentMonthDay: {
    color: "#676767",
  },
  highlight: {
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  firstHighlight: {
    extend: "highlight",
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
  },
  endHighlight: {
    extend: "highlight",
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  },
}));

export default withStyles(styles)(WeekPicker);
